/**
 * Top-up or platform-granted credits expire soon. One notice per tenant per sweep, listing every grant
 * that fell inside the lead time; plan credits are left out (the balance card shows their date).
 */

import type { TenantEventDefinition } from './definition'

export const BILLING_CREDITS_EXPIRING_EVENT_TYPE = 'billing_credits_expiring' as const

/** Days ahead of an expiry the notice goes out. */
export const CREDITS_EXPIRING_LEAD_DAYS_PARAMETER = 'leadDays'

export interface ExpiringCreditsGrant {
  /** The crediting operation: `topup` or `manual_grant`. */
  source: string
  remainingMicrousd: number
  expiresAt: string
}

export interface BillingCreditsExpiringPayload {
  totalMicrousd: number
  earliestExpiresAt: string
  grants: ExpiringCreditsGrant[]
}

/** Credits expiring at the earliest moment: the subject names these, not the total. */
export function earliestExpiringMicrousd({ earliestExpiresAt, grants }: BillingCreditsExpiringPayload): number {
  return grants
    .filter((grant) => grant.expiresAt === earliestExpiresAt)
    .reduce((sum, grant) => sum + grant.remainingMicrousd, 0)
}

function isGrant(value: unknown): value is ExpiringCreditsGrant {
  if (typeof value !== 'object' || value === null) return false
  const g = value as Record<string, unknown>
  return typeof g.source === 'string' && typeof g.remainingMicrousd === 'number' && typeof g.expiresAt === 'string'
}

function isCreditsExpiringPayload(payload: unknown): payload is BillingCreditsExpiringPayload {
  if (typeof payload !== 'object' || payload === null) return false
  const p = payload as Record<string, unknown>
  return (
    typeof p.totalMicrousd === 'number' &&
    typeof p.earliestExpiresAt === 'string' &&
    Array.isArray(p.grants) &&
    p.grants.length > 0 &&
    p.grants.every(isGrant)
  )
}

export const billingCreditsExpiringEventDefinition: TenantEventDefinition<BillingCreditsExpiringPayload> = {
  eventType: BILLING_CREDITS_EXPIRING_EVENT_TYPE,
  supportedChannelTypes: ['email', 'telegram'],
  validatePayload: isCreditsExpiringPayload,
  tenant: {
    label: 'Credits expiring soon',
    description: 'Top-up or platform-granted credits expire within the lead time; sent once per grant.',
    recipients: 'account_alerts',
    mandatory: false,
    parameters: [
      {
        key: CREDITS_EXPIRING_LEAD_DAYS_PARAMETER,
        label: 'Days before expiry',
        description: 'How many days ahead tenants are told.',
        min: 1,
        max: 60,
        default: 7,
      },
    ],
  },
}
